import React, { FC, ReactNode, useState, createContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

import { canRedirectAfterSignin } from "~/libs";

import {
  useApisMembersProfilesShow,
  useApisMembersNavigationItemsIndex,
} from "~/hooks";

import { NavigationAccessGuard } from "~/components/guards/NavigationAccessGuard";

import {
  CompanyContractPlanType,
  EmployeeProfileType,
  EmployeeRoleType,
  NavigationItemCompanyType,
} from "~/domains";

import { Loading } from "~/ui";

export const CurrentEmployeeContext = createContext<{
  currentEmployee?: EmployeeProfileType;
  refetch: () => void;
}>({
  currentEmployee: undefined,
  refetch: () => "",
});

type PropsType = {
  accessAbleRole: EmployeeRoleType[];
  accessAblePlan: CompanyContractPlanType[];
  children: ReactNode;
};

export const CurrentEmployeeProvider: FC<PropsType> = ({
  accessAbleRole,
  accessAblePlan,
  children,
}: PropsType) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentEmployee, setCurrentEmployee] = useState<EmployeeProfileType>();
  const [navigationData, setNavigationData] =
    useState<NavigationItemCompanyType[]>();

  const { refetch: refetchNavigationData } = useApisMembersNavigationItemsIndex(
    {
      config: {
        enabled: false,
        onSuccess: (data) => {
          setNavigationData(data.navigationItems);
        },
      },
    },
  );

  const onError = (error: unknown) => {
    Sentry.captureException(error);
    error instanceof Error && toast.error(error.message);
    const redirectURL = canRedirectAfterSignin(location.pathname)
      ? `/sign_in?redirect_url=${location.pathname}`
      : "/sign_in";
    navigate(redirectURL);
  };

  const onSuccess = (data: EmployeeProfileType) => {
    setCurrentEmployee(data);

    // Sentry設定
    Sentry.getCurrentScope().setUser({
      id: data.id,
      company: data.company,
      employeeRole: data.employeeRole,
      name: `${data.lastName} ${data.firstName}`,
      katakanaName: `${data.katakanaLastName} ${data.katakanaFirstName}`,
      jobCategory: data.jobCategory,
      jobTitle: data.jobTitle,
      employeeCode: data.employeeCode,
      host: window.location.host,
    });

    // ロール・プランベースのアクセス制御チェック
    const hasRoleAccess = accessAbleRole.some(
      (role) => role.id === data.employeeRole.id,
    );
    const hasPlanAccess = accessAblePlan.some(
      (plan) => plan.id === data.company.contractPlan.id,
    );

    if (!hasRoleAccess || !hasPlanAccess) {
      const error = new Error("アクセス権限がありません");
      Sentry.captureException(error);
      toast.error(error.message);
      navigate("/profile");
    } else {
      void refetchNavigationData();
    }
  };

  const { refetch } = useApisMembersProfilesShow({
    config: {
      onSuccess,
      onError,
    },
  });

  if (!currentEmployee || !navigationData) return <Loading className="mt-36" />;

  return (
    <CurrentEmployeeContext.Provider value={{ currentEmployee, refetch }}>
      <NavigationAccessGuard
        currentEmployee={currentEmployee}
        navigationItems={navigationData}
      >
        {children}
      </NavigationAccessGuard>
    </CurrentEmployeeContext.Provider>
  );
};
